import {
  Add,
  Business,
  Close,
  Description,
  Key,
  Lightbulb,
  Mic,
  Monitor,
  Phone,
  Psychology,
  Work
} from '@mui/icons-material';
import { Box, Button, Card, CardContent, Container, Dialog, DialogContent, Grid, IconButton, Typography, useMediaQuery } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

// Custom Organization Logo Component for the central card
const OrganizationLogoIcon = ({ sx }) => (
  <Business
    sx={{
      fontSize: { xs: 36, md: 44 },
      color: '#2f62ff',
      filter: 'drop-shadow(0 2px 4px rgba(47,98,255,0.35))',
      ...sx
    }}
  />
);

const CPUSection = () => {
  const { t } = useTranslation();
  const isSmall = useMediaQuery('(max-width:500px)');
  const isMobile = useMediaQuery('(max-width:600px)');
  const isTablet = useMediaQuery('(max-width:900px)');
  const [openModal, setOpenModal] = useState(false);

  // Handler for navigating to product detail page
  const handleFeatureClick = (productId) => {
    if (productId) {
      window.location.href = `/detailed-product?product=${productId}`;
    }
  };

  const aiFeatures = [
    {
      icon: <Work sx={{ fontSize: { xs: 32, sm: 36, md: 40 } }} />,
      title: t('smartOrg.features.contentWriting'),
      color: '#7c3aed',
      position: { bottom: '10%', right: '15%' },
      productId: 5, // Smart Organization - OrgJobs AI is part of it
    },
    {
      icon: <Phone sx={{ fontSize: { xs: 32, sm: 36, md: 40 } }} />,
      title: t('smartOrg.features.imageGenerator'),
      color: '#f97316',
      position: { top: '10%', right: '15%' },
      productId: 2, // AI-Enabled Call Center
    },
    {
      icon: <Psychology sx={{ fontSize: { xs: 32, sm: 36, md: 40 } }} />,
      title: t('smartOrg.features.codeGenerator'),
      color: '#8b5cf6',
      position: { top: '50%', left: '-5%', transform: 'translateY(-50%)' },
      hoverTransform: 'translateY(-50px) scale(1.05)',
      productId: 5, // Smart Organization - OrgChat AI is part of it
    },
    {
      icon: <Monitor sx={{ fontSize: { xs: 32, sm: 36, md: 40 } }} />,
      title: t('smartOrg.features.chatBot'),
      color: '#06b6d4',
      position: { top: '50%', right: '-5%', transform: 'translateY(-50%)' },
      hoverTransform: 'translateY(-50px) scale(1.05)',
      productId: 1, // Intelligent Monitoring System
    },
    {
      icon: <Description sx={{ fontSize: { xs: 32, sm: 36, md: 40 } }} />,
      title: t('smartOrg.features.textToAudio'),
      color: '#3b82f6',
      position: { bottom: '10%', left: '15%' },
      productId: 4, // Textra
    },
    {
      icon: <Mic sx={{ fontSize: { xs: 32, sm: 36, md: 40 } }} />,
      title: t('smartOrg.features.speechToText'),
      color: '#ef4444',

      position: { top: '10%', left: '15%' },
      productId: 3, // Meeting Assistant
    },
  ];

  const decorativeElements = [
    { top: '15%', left: '8%' },
    { top: '20%', right: '45%' },
    { top: '30%', left: '48%' },
    { top: '45%', right: '28%' },
    { bottom: '25%', left: '38%' },
    { bottom: '15%', right: '8%' },
    { bottom: '45%', left: '18%' },
    { top: '60%', right: '75%' },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        background: '#111022',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: { xs: 6, sm: 8, md: 12 },
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      {/* Decorative plus signs */}
      {decorativeElements.map((pos, index) => (
        <Add
          key={index}
          sx={{
            position: 'absolute',
            ...pos,
            fontSize: { xs: 16, md: 24 },
            color: 'rgba(255, 255, 255, 0.1)',
            animation: `twinkle ${3 + index * 0.5}s ease-in-out infinite`,
            '@keyframes twinkle': {
              '0%, 100%': { opacity: 0.1 },
              '50%': { opacity: 0.3 },
            },
          }}
        />
      ))}

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header Text */}
        <Box sx={{ textAlign: 'center', mb: 8, mt: { xs: 8, sm: 6, md: 4 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3 }}>
            <IconButton
              onClick={() => setOpenModal(true)}
              sx={{
                color: '#fbbf24',
                transition: 'all 0.3s ease',
                '&:hover': {
                  color: '#f59e0b',
                  transform: 'scale(1.1)',
                },
              }}
            >
              <Lightbulb sx={{ fontSize: { xs: 32, md: 40 } }} />
            </IconButton>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem', lg: '3.5rem' },
                fontWeight: 'bold',
                color: 'white',
                lineHeight: 1.3,
              }}
            >
              {t('smartOrg.headline')}
            </Typography>
          </Box>

          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
              color: 'rgba(255, 255, 255, 0.8)',
              maxWidth: { xs: '98%', sm: '100%', md: '1600px', lg: '1800px' },
              mx: 'auto',
              mb: 4,
              lineHeight: 1.6,
              textAlign: 'justify',
              px: { xs: 1, sm: 2, md: 1 },
            }}
          >
            {t('smartOrg.description')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<Add />}
              onClick={() => window.location.href = '/core-modules'}
              className="learn-more-ar"
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 600,
                background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)',
                boxShadow: '0 8px 24px rgba(99, 102, 241, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(90deg, #4f46e5 0%, #7c3aed 100%)',
                  boxShadow: '0 12px 32px rgba(99, 102, 241, 0.6)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {t('smartOrg.cta')}
            </Button>

            <Button
              variant="contained"
              size="large"
              startIcon={<Monitor />}
              onClick={() => window.open('https://smartorg.demisco.ai/ai-agent/chat', '_blank')}
              className="demo-request-ar"
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 600,
                background: 'linear-gradient(90deg, #06b6d4 0%, #0891b2 100%)',
                boxShadow: '0 8px 24px rgba(6, 182, 212, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(90deg, #0891b2 0%, #0e7490 100%)',
                  boxShadow: '0 12px 32px rgba(6, 182, 212, 0.6)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {t('header.demo')}
            </Button>
          </Box>
        </Box>

        {/* AI Features Layout - responsive */}
        {isSmall ? (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {aiFeatures.map((feature, index) => (
              <Grid item xs={6} key={index}>
                <Card
                  onClick={() => handleFeatureClick(feature.productId)}
                  sx={{
                    background: 'linear-gradient(180deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.04) 100%)',
                    backdropFilter: 'blur(14px) saturate(140%)',
                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    border: '1px solid rgba(255, 255, 255, 0.16)',
                    borderRadius: 3,
                    overflow: 'hidden',
                    transition: 'transform 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      borderColor: feature.color,
                      boxShadow: `0 12px 30px ${feature.color}33, 0 6px 16px rgba(0,0,0,0.3)`,
                    },
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 2 }}>
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: 2,
                        background: `${feature.color}22`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 1.5,
                        color: feature.color,
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        lineHeight: 1.3,
                      }}
                    >
                      {feature.title}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box
            sx={{
              position: 'relative',
              height: { xs: '600px', md: '700px' },
              maxWidth: '900px',
              mx: 'auto',
            }}
          >
            {/* Central AI Chip */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                zIndex: 2,
              }}
            >
              {/* Outer rounded tile (app-icon style) */}
              <Box
                onClick={() => handleFeatureClick(5)}
                sx={{
                  position: 'relative',
                  width: { xs: 160, md: 200 },
                  height: { xs: 160, md: 200 },
                  borderRadius: 6,
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  //   'radial-gradient(120% 120% at 30% 0%, #0f2a2f 0%, #1f3f43 40%, #203d44 60%, #0e121f 100%)',
                  boxShadow:
                    'inset 0 1px 0 rgba(255,255,255,0.15), 0 22px 60px rgba(0,0,0,0.6)',
                  border: '1px solid rgba(255,255,255,0.25)',
                  mb: 2,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'transform 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: -12,
                    background: 'radial-gradient(60% 60% at 50% 50%, #6366f133 0%, transparent 60%)',
                    filter: 'blur(18px)',
                    opacity: 0.35,
                    transition: 'opacity 0.35s ease, transform 0.35s ease',
                    zIndex: 0,
                  },
                  '&:hover': {
                    transform: 'translateY(-8px) scale(1.05)',
                    borderColor: '#6366f1',
                    boxShadow: '0 16px 50px rgba(99,102,241,0.35), 0 6px 18px rgba(0,0,0,0.35)',
                  },
                  '&:hover::before': {
                    opacity: 0.7,
                    transform: 'scale(1.06)',
                  },
                }}
              >
                {/* hairline inner stroke */}
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 6,
                    borderRadius: 5,
                    border: '1px solid rgba(255,255,255,0.35)',
                    opacity: 0.6,
                  }}
                />

                {/* Central chip body with pins */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: 108, md: 132 },
                    height: { xs: 108, md: 132 },
                  }}
                >
                  {/* Pins */}
                  {[...Array(12)].map((_, i) => (
                    <Box
                      key={i}
                      sx={{
                        position: 'absolute',
                        width: 18,
                        height: 6,
                        background: '#e6ebf1',
                        borderRadius: 2,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
                        ...(i < 3 && { top: 10 + i * 26, left: -22 }),
                        ...(i >= 3 && i < 6 && { top: 10 + (i - 3) * 26, right: -22 }),
                        ...(i >= 6 && i < 9 && { bottom: 10 + (i - 6) * 26, left: -22 }),
                        ...(i >= 9 && { bottom: 10 + (i - 9) * 26, right: -22 }),
                      }}
                    />
                  ))}

                  {/* Chip core with thick white ring and dark center */}
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      borderRadius: 4,
                      background: '#e9edf2',
                      boxShadow: 'inset 0 6px 18px rgba(0,0,0,0.18), 0 6px 16px rgba(0,0,0,0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 64, md: 78 },
                        height: { xs: 64, md: 78 },
                        borderRadius: 2.5,
                        background:
                          'radial-gradient(100% 100% at 50% 30%, #15172b 0%, #0b0c17 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)'
                      }}
                    >
                      <OrganizationLogoIcon />
                    </Box>
                  </Box>
                </Box>
              </Box>
              {/* Hover gradient text sync */}
              <Box sx={{ height: 0, '&:hover + .ai-pack-label': {} }} />
              <Typography
                className="ai-pack-label"
                variant="h5"
                sx={{
                  fontWeight: 'bold',
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                  // start as neutral white, animate to gradient when hovered like other cards
                  background: 'linear-gradient(90deg, #ffffffcc 0%, #ffffffcc 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  transition: 'background 0.35s ease',
                }}
              >
                {t('smartOrg.aiPack')}
              </Typography>
            </Box>

            {/* Feature Cards */}
            {aiFeatures.map((feature, index) => (
              <Card
                key={index}
                onClick={() => handleFeatureClick(feature.productId)}
                sx={{
                  position: 'absolute',
                  ...feature.position,
                  width: { xs: 140, md: 160 },
                  // Glassmorphism base styles
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.04) 100%)',
                  backdropFilter: 'blur(14px) saturate(140%)',
                  backgroundColor: 'rgba(255, 255, 255, 0.02)',
                  border: '1px solid rgba(255, 255, 255, 0.16)',
                  borderRadius: 3,
                  overflow: 'hidden',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)',
                  transition: 'transform 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease',
                  cursor: 'pointer',
                  // Ambient glow
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: -12,
                    background: `radial-gradient(60% 60% at 50% 50%, ${feature.color}33 0%, transparent 60%)`,
                    filter: 'blur(18px)',
                    opacity: 0.35,
                    transition: 'opacity 0.35s ease, transform 0.35s ease',
                    zIndex: 0,
                  },
                  '&:hover': {
                    transform: feature.hoverTransform ? feature.hoverTransform : 'translateY(-8px) scale(1.05)',
                    borderColor: feature.color,
                    boxShadow: feature.hoverTransform
                      ? `0 16px 50px ${feature.color}55, 0 10px 25px rgba(0,0,0,0.45)`
                      : `0 16px 50px ${feature.color}33, 0 6px 18px rgba(0,0,0,0.35)`,
                  },
                  '&:hover::before': {
                    opacity: 0.7,
                    transform: 'scale(1.06)',
                  },
                }}
              >
                <CardContent
                  sx={{
                    textAlign: 'center',
                    p: 3,
                    '&:last-child': { pb: 3 },
                  }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 2,
                      background: `${feature.color}22`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                      color: feature.color,
                      position: 'relative',
                      boxShadow: `0 10px 25px ${feature.color}22, 0 0 0 1px ${feature.color}33 inset`,
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        borderRadius: 6,
                        boxShadow: `0 0 24px ${feature.color}55`,
                        opacity: 0.35,
                        filter: 'blur(8px)',
                        zIndex: -1,
                      },
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'white',
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      lineHeight: 1.3,
                    }}
                  >
                    {feature.title}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Container>

      {/* Modal for Key Objectives */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
          },
        }}
      >
        <DialogContent sx={{ p: 4, position: 'relative' }}>
          {/* Close Button */}
          <IconButton
            onClick={() => setOpenModal(false)}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': {
                color: 'white',
                background: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <Close />
          </IconButton>

          {/* Icon */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Key sx={{ fontSize: 60, color: '#fbbf24' }} />
          </Box>

          {/* Title */}
          <Typography
            variant="h4"
            sx={{
              textAlign: 'center',
              color: 'white',
              fontWeight: 'bold',
              mb: 4,
              fontSize: { xs: '1.5rem', md: '2rem' },
            }}
          >
            {t('smartOrg.keyObjectives.title')}
          </Typography>

          {/* Objectives List */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {[
              { key: 'digitization', icon: '🔄' },
              { key: 'centralization', icon: '📊' },
              { key: 'empowerment', icon: '💪' },
              { key: 'decision', icon: '📈' },
            ].map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2.5,
                  borderRadius: 2,
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.1)',
                    transform: 'translateX(8px)',
                    borderColor: '#fbbf24',
                  },
                }}
              >
                <Box
                  sx={{
                    fontSize: '2rem',
                    minWidth: 40,
                    textAlign: 'center',
                  }}
                >
                  {item.icon}
                </Box>
                <Typography
                  sx={{
                    color: 'white',
                    fontSize: { xs: '1rem', md: '1.125rem' },
                    fontWeight: 500,
                  }}
                >
                  {t(`smartOrg.keyObjectives.${item.key}`)}
                </Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CPUSection;

